/** Tiny class-name joiner (falsy values are skipped). */
export function cn(...parts: unknown[]): string {
  return parts.filter((p): p is string => typeof p === 'string' && p.length > 0).join(' ');
}

/** Turkish-aware uppercase (i → İ, ı → I). Always use this for all-caps UI text. */
export function upper(text: string): string {
  return text.toLocaleUpperCase('tr');
}

/** Team identifiers used across the kit. `J` = neutral jury. */
export type Team = 'A' | 'B';
export type Side = Team | 'J';

export const TEAM_COLOR: Record<Side, string> = {
  A: 'var(--color-team-a)',
  B: 'var(--color-team-b)',
  J: 'var(--color-jury)',
};

export const TEAM_TINT: Record<Side, string> = {
  A: 'var(--color-team-a-tint)',
  B: 'var(--color-team-b-tint)',
  J: 'var(--color-jury-tint)',
};

/** Formats a score/points value with at most one decimal (7 → "7", 7.5 → "7.5"). */
export function formatScore(value: number, decimals = 1): string {
  const fixed = value.toFixed(decimals);
  return decimals > 0 ? fixed.replace(/\.0+$/, '') : fixed;
}

/** Deterministic 32-bit string hash (FNV-1a) for stable pseudo-random visuals. */
export function hashString(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Small seeded PRNG (mulberry32). */
export function seededRandom(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
